import { call, put, select, takeLatest } from "redux-saga/effects";
import { generatePath } from "react-router-dom";
import RoutePaths from "routes/route-paths";
import BackendClient from "api/backend-client";
import { createProcessDataFromBatchFailure, createProcessDataFromBatchSuccessful, createSessionFailure, createSessionSuccessful, deleteTimeSeriesFileFailure, deleteTimeSeriesFileSuccessful, removeSessionFailure, removeSessionSuccessful, ProcessDataActionType, setSession, setState, uploadMetadataFailure, uploadMetadataSuccessful, uploadTimeSeriesFailure, uploadTimeSeriesSuccessful, setUploadingTimeSeries, setUploadingMetadata, deleteMetadataFileSuccessful, deleteMetadataFileFailure, } from "./process-data.actions";
import { RecipeScalingApiMethods } from "api/api-methods";
import { getSessionIdSelector } from "./process-data.selectors";
import { setCurrentRequestTemplate, setupKnowledgeBaseItem, } from "store/knowledge-base-item/knowledge-base-item.actions";
import { RequestTemplate } from "store/knowledge-base-item/knowledge-base-item.types";
import { waitForLogin } from "store/auth/auth.saga";
import { connectChannelSaga } from "store/knowledge-base-item/knowledge-base-item.saga";
import { GlobalEntityType } from "shared/types";
import { apiErrorHandler } from "store/global/global.saga";
import { navigate } from "shared/navigation";
function* createSessionSaga() {
    try {
        yield call(waitForLogin);
        const backendClient = BackendClient.getInstance();
        const { sessionId, subscriptionId } = yield call([
            backendClient,
            RecipeScalingApiMethods.CreateProcessDataSession,
        ]);
        yield put(setupKnowledgeBaseItem({
            id: subscriptionId,
            name: "",
            description: "",
            tags: [],
            isCompleted: false,
            lastSavedDate: "",
            createdAt: "",
            createdBy: null,
            lastSavedBy: null,
            hasUnsavedChanges: false,
            entityType: GlobalEntityType.ProcessData,
            owners: [],
        }));
        yield put(setSession(sessionId));
        yield put(createSessionSuccessful());
        yield call(connectChannelSaga);
    }
    catch (e) {
        yield call(apiErrorHandler, e, "process data start session failed");
        yield put(createSessionFailure());
    }
}
function* removeSessionSaga() {
    try {
        yield call(waitForLogin);
        const backendClient = BackendClient.getInstance();
        const sessionId = yield select(getSessionIdSelector);
        if (sessionId !== null) {
            yield call([backendClient, RecipeScalingApiMethods.RemoveProcessDataSession], sessionId);
            yield put(setSession(null));
            yield put(removeSessionSuccessful());
        }
    }
    catch (e) {
        yield call(apiErrorHandler, e, "process data end session failed");
        yield put(removeSessionFailure());
    }
}
function* uploadTimeSeriesSaga({ payload }) {
    try {
        yield call(waitForLogin);
        const backendClient = BackendClient.getInstance();
        const sessionId = yield select(getSessionIdSelector);
        if (sessionId !== null) {
            const files = Array.from(payload.values());
            yield put(setUploadingTimeSeries(files.map((file) => file.name)));
            const state = yield call([backendClient, RecipeScalingApiMethods.UploadTimeSeries], sessionId, payload);
            yield put(setUploadingTimeSeries([]));
            yield put(setState(state));
            yield put(uploadTimeSeriesSuccessful());
        }
    }
    catch (e) {
        yield call(apiErrorHandler, e, "upload timeseries failed");
        yield put(setUploadingTimeSeries([]));
        yield put(uploadTimeSeriesFailure());
    }
}
function* uploadMetadataSaga({ payload }) {
    try {
        yield call(waitForLogin);
        const backendClient = BackendClient.getInstance();
        const sessionId = yield select(getSessionIdSelector);
        if (sessionId !== null) {
            const files = Array.from(payload.values());
            yield put(setUploadingMetadata(files.map((file) => file.name)));
            const state = yield call([backendClient, RecipeScalingApiMethods.UploadMetadata], sessionId, payload);
            yield put(setUploadingMetadata([]));
            yield put(setState(state));
            yield put(uploadMetadataSuccessful());
        }
    }
    catch (e) {
        yield call(apiErrorHandler, e, "upload metadata failed");
        yield put(setUploadingMetadata([]));
        yield put(uploadMetadataFailure());
    }
}
function* deleteTimeSeriesFileSaga({ payload }) {
    try {
        yield call(waitForLogin);
        const backendClient = BackendClient.getInstance();
        const sessionId = yield select(getSessionIdSelector);
        if (sessionId !== null) {
            const state = yield call([backendClient, RecipeScalingApiMethods.DeleteTimeSeriesFile], sessionId, payload);
            yield put(setState(state));
            yield put(deleteTimeSeriesFileSuccessful());
        }
    }
    catch (e) {
        yield call(apiErrorHandler, e, "delete timeseries file failed");
        yield put(deleteTimeSeriesFileFailure());
    }
}
function* deleteMetadataFileSaga({ payload }) {
    try {
        yield call(waitForLogin);
        const backendClient = BackendClient.getInstance();
        const sessionId = yield select(getSessionIdSelector);
        if (sessionId !== null) {
            const state = yield call([backendClient, RecipeScalingApiMethods.DeleteMetadataFile], sessionId, payload);
            yield put(setState(state));
            yield put(deleteMetadataFileSuccessful());
        }
    }
    catch (e) {
        yield call(apiErrorHandler, e, "delete metadata file failed");
        yield put(deleteMetadataFileFailure());
    }
}
function* createProcessDataFromBatchSaga({ payload }) {
    try {
        yield call(waitForLogin);
        const backendClient = BackendClient.getInstance();
        const sessionId = yield select(getSessionIdSelector);
        if (sessionId !== null) {
            yield put(setCurrentRequestTemplate(RequestTemplate.CREATE_PROCESS_DATA_FROM_BATCH));
            // todo: update process data lists items from the response(will be implemented in the another story)
            yield call([backendClient, RecipeScalingApiMethods.CreateProcessDataFromBatch], sessionId, payload);
            yield put(setSession(null));
            yield put(createProcessDataFromBatchSuccessful());
            yield call(navigate, generatePath(RoutePaths.ProcessDatas));
        }
    }
    catch (e) {
        yield call(apiErrorHandler, e, "create process data from batches failed");
        yield put(createProcessDataFromBatchFailure());
    }
}
export default function* processDataSaga() {
    yield takeLatest(ProcessDataActionType.CREATE_SESSION, createSessionSaga);
    yield takeLatest(ProcessDataActionType.REMOVE_SESSION, removeSessionSaga);
    yield takeLatest(ProcessDataActionType.UPLOAD_TIMESERIES, uploadTimeSeriesSaga);
    yield takeLatest(ProcessDataActionType.UPLOAD_METADATA, uploadMetadataSaga);
    yield takeLatest(ProcessDataActionType.DELETE_TIMESERIES_FILE, deleteTimeSeriesFileSaga);
    yield takeLatest(ProcessDataActionType.DELETE_METADATA_FILE, deleteMetadataFileSaga);
    yield takeLatest(ProcessDataActionType.CREATE_PROCESS_DATA_FROM_BATCH, createProcessDataFromBatchSaga);
}
